import React, { PropsWithChildren, useEffect, useRef, useState, type JSX } from 'react';

interface IReadMore {
  children?: React.ReactNode;
  clampLimit?: number;
  backgroundColor?: string;
  isCondensed?: boolean;
  seeMoreText: string;
  seeLessText: string;
}

const ReadMore = ({
  children,
  backgroundColor = '#FFF',
  clampLimit = 5,
  isCondensed = false,
  seeMoreText,
  seeLessText,
}: PropsWithChildren<IReadMore>): JSX.Element => {
  const [isReadMore, toggleReadMore] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const readMoreContent = useRef<HTMLDivElement>(null);
  const isDeployed = isDisabled || isReadMore;

  useEffect(() => {
    if (readMoreContent.current) {
      const diff =
        readMoreContent.current.scrollHeight -
        readMoreContent.current.clientHeight;

      // Disable the toogle if a diff of height between the full and truncated version is detected
      // 10px (instead of 0) is arbitrarily chosen in case some unexpected margins are added
      setIsDisabled(diff < 10);
    }
  }, []);

  return (
    <>
      <div className="read-more">
        <div className="read-more-content" ref={readMoreContent}>
          {children}
        </div>
        {isDisabled ? null : (
          <button
            className="read-more-btn"
            type="button"
            onClick={() => toggleReadMore(!isReadMore)}
            aria-hidden="true"
          >
            {!isReadMore ? seeMoreText : seeLessText}
          </button>
        )}
      </div>
      <style jsx>{`
        .read-more {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: start;
        }

        .read-more-content {
          --readmore-line-height: 1.4rem;

          position: relative;
          overflow: hidden;
          max-height: ${isDeployed
            ? 'none'
            : `calc(var(--readmore-line-height) * ${clampLimit})`};
        }

        .read-more-content :global(p),
        .read-more-content :global(ul),
        .read-more-content :global(ol) {
          margin: var(--readmore-line-height) 0 0;
          line-height: var(--readmore-line-height);
          ${!isDeployed ? 'margin-top: 0;' : ''}
        }

        .read-more-content :global(p:first-child),
        .read-more-content :global(ul:first-child),
        .read-more-content :global(ol:first-child) {
          margin-top: 0;
        }

        .read-more-content:after {
          ${!isDeployed
            ? `
           content: '';
          position: absolute;
          bottom: 0;
          right: 0;
          width: 100%;
          pointer-events: none;
          background: linear-gradient(rgba(255,255,255,0), ${backgroundColor});
          height: 3em;
        `
            : ''}
        }

        .read-more-btn {
          margin-top: 0.3rem;
          border: none;
          background: none;
          color: var(--clr-tertiary-1);
          font-size: var(--font-size-2);
          text-decoration: underline;
          appearance: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .read-more-btn:hover {
          text-decoration: none;
        }

        @media (min-width: 801px) {
          .read-more-content {
            margin-bottom: ${isDeployed && isCondensed && !isDisabled
              ? `var(--readmore-line-height)`
              : 0};
          }

          .read-more-content:after {
            background: red;
            ${!isDeployed
              ? `
            width: ${isCondensed ? '60%' : '100%'};
            height: ${isCondensed ? '20px' : '3em'};
            background: ${
              isCondensed
                ? `linear-gradient(to left, ${backgroundColor} 7rem, rgba(255,255,255,0))`
                : `linear-gradient(rgba(255,255,255,0), ${backgroundColor})`
            };
          `
              : ''}
          }

          .read-more-btn {
            ${isCondensed
              ? `
            position: absolute;
            bottom: 0;
            right: 0;
          `
              : ''}
          }
        }
      `}</style>
    </>
  );
};

export default ReadMore;
